import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { ProjectProfileForm } from "./ProjectProfileForm";
import { EmailSignatureForm } from "./EmailSignatureForm";
import { ColumnFilterItem } from "antd/es/table/interface";
import { sortBy, uniqBy } from "lodash";
import { userSettingsStore } from "Stores/UserSettingsStore";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { Table } from "Components/General/Table";
import { ProjectProfilesExpandedRow } from "Components/ProjectProfiles/ProjectProfilesExpandedRow";

export const ProjectProfiles = withModal(({ showModal }: WithModal) => {
  const { t } = useTranslation(I18N_NS.USER_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<API_LinkedUserDetails[]>([]);
  const [assignedProjectsFilters, setAssignedProjectsFilters] = useState<ColumnFilterItem[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await API.getData<API_LinkedUserDetails[]>(API_URL.PAVE_USERS.getLinkedUserDetails());
      setData(response);
      const projectFilterOptions = response.reduce<ColumnFilterItem[]>((acc, curr) => {
        if (curr.assignedProjects?.length) {
          acc.push(
            ...curr.assignedProjects.map((project) => ({
              text: project.name,
              value: project.id,
            }))
          );
        }
        return acc;
      }, []);
      setAssignedProjectsFilters(uniqBy(sortBy(projectFilterOptions, "text"), "value"));
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (id: number, values: API_UserContactData) => {
    await API.editData(API_URL.PAVE_USERS.updateLinkedUsersContact(id), values);
    await userSettingsStore.fetchSilently();
    fetchData();
    notificationSuccess(t("Success"), t("Contact information has been saved"));
  };

  const saveMailSignature = async (id: number, values: API_UserSettings) => {
    await API.editData(API_URL.PAVE_USERS.updateLinkedUserSettings(id), values);
    await userSettingsStore.fetchSilently();
    fetchData();
    notificationSuccess(t("Success"), t("Signature saved"));
  };

  const handleEditProfile = (record: API_LinkedUserDetails) => {
    showModal(ProjectProfileForm, {
      modalProps: {
        footer: null,
        width: 1000,
        title: t("Edit profile"),
      },
      componentProps: {
        initialData: { ...record.userContactData },
        onSubmit: (values) => saveProfile(record.id, values),
      },
    });
  };

  const handleEditSignature = (record: API_LinkedUserDetails) => {
    showModal(EmailSignatureForm, {
      modalProps: {
        footer: null,
        width: 1000,
        title: t("Edit signature"),
      },
      componentProps: {
        initialData: {
          mailSignature: record.mailSignature,
          mailSignatureOption: record.mailSignatureOption,
        },
        onSubmit: (signature) => saveMailSignature(record.id, signature),
      },
    });
  };

  const columns: ColumnsType<API_LinkedUserDetails> = [
    {
      title: t("Old login"),
      dataIndex: "userId",
      sorter: (a, b) => a.userId?.localeCompare(b.userId || ""),
    },
    {
      title: t("Email"),
      dataIndex: ["userContactData", "email"],
      sorter: (a, b) => a.userContactData.email.localeCompare(b.userContactData.email),
      ellipsis: true,
      width: 180,
    },
    {
      title: t("Position"),
      dataIndex: ["userContactData", "companyRole"],
      sorter: (a, b) => a.userContactData.companyRole.localeCompare(b.userContactData.companyRole),
      ellipsis: true,
    },
    {
      title: t("Company"),
      dataIndex: ["userContactData", "companyName"],
      sorter: (a, b) => a.userContactData.companyName.localeCompare(b.userContactData.companyName),
      ellipsis: true,
    },
    {
      title: t("Address"),
      dataIndex: "address",
      ellipsis: { showTitle: false },
      render: (value, record) => {
        const { street, postalCode, city /*phoneNumber, mobileNumber, fax */ } = record.userContactData;
        const title = [postalCode, city, street].filter(Boolean).join(", ");
        return (
          <Tooltip
            placement="topLeft"
            title={title}
            styles={{
              root: { maxWidth: 400 },
            }}
          >
            {title}
          </Tooltip>
        );
      },
    },
    {
      title: t("Projects"),
      dataIndex: "assignedProjects",
      render: (value: API_ProjectShortInfo[]) => {
        const title = value.map((project) => `${project.name} (PID: ${project.id})`);
        return (
          <Tooltip
            placement="topLeft"
            title={title.map((t) => (
              <div key={t}>{t}</div>
            ))}
            styles={{
              root: { maxWidth: 400 },
            }}
          >
            {title.join(", ")}
          </Tooltip>
        );
      },
      width: 200,
      ellipsis: { showTitle: false },
      filters: assignedProjectsFilters,
      filterMultiple: true,
      onFilter: (value, record) => record.assignedProjects?.some((p) => p.id === value),
      filterSearch: true,
      sorter: (a, b) =>
        a.assignedProjects
          ?.map((p) => `${p.name} (PID: ${p.id})`)
          .join(",")
          .localeCompare(b.assignedProjects?.map((p) => `${p.name} (PID: ${p.id})`).join(",")),
    },
    {
      title: t("Action"),
      key: "id",
      fixed: true,
      width: 100,
      render: (value, record) => (
        <Space>
          <Tooltip title={t("Edit profile")}>
            <Button size="small" type="text" onClick={() => handleEditProfile(record)}>
              <FontAwesomeIcon icon={["far", "user-edit"]} fixedWidth={true} />
            </Button>
          </Tooltip>
          <Tooltip title={t("Edit signature")}>
            <Button size="small" type="text" onClick={() => handleEditSignature(record)}>
              <FontAwesomeIcon icon={["far", "signature"]} fixedWidth={true} />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Table
      name="projectProfiles"
      loading={loading}
      virtual
      scroll={{ x: 1000, y: 500 }}
      rowKey="id"
      pagination={false}
      dataSource={data}
      columns={columns}
      rowClassName={(record) => (record.active ? "" : "disabledCell")}
      expandable={{
        columnWidth: 50,
        expandedRowRender: (record) => (
          <ProjectProfilesExpandedRow
            projects={record.assignedProjects}
            title={t("Projects")}
            passiveMemberTooltip={t("Passive member: No active access, project will not be shown on the dashboard.")}
            archivedProjectTooltip={t("Archived project: Read-only access")}
          />
        ),
        rowExpandable: (record) => record.assignedProjects?.length > 1,
      }}
    />
  );
});
